
import { createElement, lazy, useEffect, useMemo, useState } from "react";
import {
    Menu,
    useResourceDefinitions,

    ResourceDefinition,
    useCreatePath,
    useGetResourceLabel,
    useResourceContext,
    useSidebarState,
    useCanAccess
} from "react-admin";
import {
    List,
    ListItem,
    ListItemText,
    Collapse,
    ListItemButton,
    ListItemIcon,
    buttonGroupClasses
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import DefaultIcon from '@mui/icons-material/ViewList';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { useLocation } from "react-router";

const groupIcons = {
    User: lazy(() => import('@mui/icons-material/PeopleOutlineOutlined')),
    Product: lazy(() => import('@mui/icons-material/SellOutlined')),
    Finance: lazy(() => import('@mui/icons-material/PaymentsOutlined')),
    Tools: lazy(() => import('@mui/icons-material/ConstructionOutlined')),
}


type Grouped = { group: string }
type AppResourceDefinition = ResourceDefinition<Grouped>
export const AppMenu = () => {
    const resources = useResourceDefinitions<Grouped>();
    const location = useLocation();
    // Group resources and separate ungrouped ones
    const { groupedResources, ungroupedResources, resourceGroupMap } = useMemo(() => {
        const groups: Record<string, AppResourceDefinition[]> = {};
        const ungrouped: AppResourceDefinition[] = [];
        const resourceGroupMap: Record<string, string> = {};

        Object.keys(resources).forEach((key) => {
            const resource = resources[key];
            if (resource.hasList) {
                const group = resource.options?.group;

                if (group) {
                    if (!groups[group]) {
                        groups[group] = [];
                    }
                    groups[group].push(resource);
                    resourceGroupMap[resource.name] = group;
                } else {
                    ungrouped.push(resource);
                }
            }
        });

        return { groupedResources: groups, ungroupedResources: ungrouped, resourceGroupMap };
    }, [resources]);

    // State for tracking open/closed groups
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    // Auto-expand the group if the current resource belongs to it
    useEffect(() => {
        const currentPath = location.pathname.split("/")[1]; // Extract resource from URL
        const currentGroup = resourceGroupMap[currentPath];

        if (currentGroup) {
            setOpenGroups((prev) => ({ ...prev, [currentGroup]: true }));
        }
    }, [location.pathname, resourceGroupMap]);

    const toggleGroup = (group: string) => {
        setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
    };

    const [sidebarOpen] = useSidebarState();

    return (
        <Menu>
            <Menu.DashboardItem />

            {/* Render grouped resources */}
            {Object.keys(groupedResources).map((group) => {

                const GroupIcon = groupIcons[group as keyof typeof groupIcons] || FolderOutlinedIcon;
                return (
                    [
                        <ListItemButton key={group} onClick={() => toggleGroup(group)}>
                            <ListItemIcon>
                                <GroupIcon />
                            </ListItemIcon>
                            <ListItemText primary={group} />
                            {openGroups[group] ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>,
                        <Collapse key={`${group}-collapse`} in={openGroups[group]} timeout="auto" unmountOnExit>
                            <List disablePadding
                                sx={{
                                    '& .MuiMenuItem-root': {
                                        pl: sidebarOpen ? 4 : 1,
                                    }
                                }}
                            >
                                {groupedResources[group].map((resource) => (
                                    <AppMenuItem resource={resource} key={resource.name} />
                                ))}
                            </List>
                        </Collapse>
                    ]
                )
            })}

            {/* Render ungrouped resources at the end */}
            {ungroupedResources.length > 0 && (
                <>
                    <ListItem>
                        <ListItemText primary="Other" />
                    </ListItem>
                    <List disablePadding
                        sx={{
                            '& .MuiMenuItem-root': {
                                pl: sidebarOpen ? 4 : 1,
                            }
                        }}
                    >
                        {ungroupedResources.map((resource) => (
                            <AppMenuItem resource={resource} key={resource.name} />

                        ))}
                    </List>
                </>
            )}
        </Menu>
    );
};


const AppMenuItem = ({ resource }: { resource: AppResourceDefinition }) => {
    const { canAccess, error, isPending } = useCanAccess({
        action: 'list',
        resource: resource?.name,
    });
    const createPath = useCreatePath();
    const getResourceLabel = useGetResourceLabel();
    if (isPending || error || canAccess == false)
        return null

    return (
        <Menu.Item 
            to={createPath({ resource: resource.name, type: 'list', })}
            state={{ _scrollToTop: true }}
            primaryText={<>{getResourceLabel(resource.name, 2)}</>}
            leftIcon={
                resource.icon ? (
                    createElement(resource.icon)
                ) : (
                    <DefaultIcon />
                )
            }
            sx={{
                '& .RaMenuItemLink-active': {
                    backgroundColor: theme => theme.palette.secondary.main,
                    color: theme => theme.palette.secondary.contrastText,
                    '& .RaMenuItemLink-icon': {
                        color: theme => theme.palette.secondary.contrastText,
                    },
                },
            }}

        />
    )
}